import { Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { graphql, useFragment, useLazyLoadQuery, useMutation } from "react-relay";
import { AnimatedDialog, AnimatedDialogContent, AnimatedDialogHeader, AnimatedDialogTitle } from "src/components/ui/animated-dialog";
import { Button } from "src/components/ui/button";
import { Input } from "src/components/ui/input";
import { InvalidFieldTooltip } from "src/components/ui/invalid-field-tooltip";
import { Label } from "src/components/ui/label";
import { LoadingSpinner } from "src/components/ui/loading-spinner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "src/components/ui/select";
import { UITourBanner } from "src/components/ui/UITourBanner";
import { useFeatureFlag } from "src/hooks/use-feature-flag";
import { useScrollToFirstInvalid } from "src/hooks/use-scroll-to-first-invalid";
import { useToast } from "src/hooks/use-toast";

import CompactSectionBuilder from "../components/CompactSectionBuilder";
import { DigestNotificationDestinations } from "../components/DigestNotificationDestinations";
import { FormSection } from "../components/FormSection";
import { ReasoningPicker } from "../components/ReasoningPicker";
import ScheduleBuilder from "../components/ScheduleBuilder";
import type { CreateDigestWizard_organization$key } from "./__generated__/CreateDigestWizard_organization.graphql";
import type { CreateDigestWizardIntegrationsQuery as CreateDigestWizardIntegrationsQueryType } from "./__generated__/CreateDigestWizardIntegrationsQuery.graphql";
import type { CreateDigestWizardMutation } from "./__generated__/CreateDigestWizardMutation.graphql";
import {
  createInitialDigestFormState,
  type DigestFormStateType,
  mapToDigestSections,
} from "./types";

const createDigestWizardFragment = graphql`
  fragment CreateDigestWizard_organization on GraphQLOrganization {
    id
  }
`;

const createDigestMutation = graphql`
  mutation CreateDigestWizardMutation($input: CreateJobInput!) {
    createJob(input: $input) {
      job {
        id
        name
      }
    }
  }
`;

const integrationsQuery = graphql`
  query CreateDigestWizardIntegrationsQuery($organizationId: GlobalID!) {
    node(id: $organizationId) {
      ... on GraphQLOrganization {
        id
        integrations {
          edges {
            node {
              integrationType
              integrationStatus
            }
          }
        }
        discordWebhooks: notificationIntegrations(kind: DISCORD_WEBHOOK) {
          edges {
            node {
              id
              name
            }
          }
        }
      }
    }
  }
`;

interface CreateDigestWizardProps {
  organization: CreateDigestWizard_organization$key;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (jobId?: string) => void;
  teamId: string;
  isOnboarding?: boolean;
  onboardingStep?: number | null;
  initialSections?: Array<{ section: string; text: string }>;
  initialName?: string;
  initialCronExpression?: string;
}

export function CreateDigestWizard({
  organization,
  isOpen,
  onClose,
  onSuccess,
  teamId,
  isOnboarding = false,
  onboardingStep = null,
  initialSections,
  initialName,
  initialCronExpression,
}: CreateDigestWizardProps) {
  const organizationData = useFragment(createDigestWizardFragment, organization);
  const { toast } = useToast();

  const [state, setState] = useState<DigestFormStateType>(() => {
    const initial = createInitialDigestFormState();
    if (initialSections && initialSections.length > 0) {
      initial.sections = mapToDigestSections(initialSections);
    }
    if (initialName) {
      initial.name = initialName;
    }
    if (initialCronExpression) {
      initial.cronExpression = initialCronExpression;
    } else if (isOnboarding) {
      initial.cronExpression = "0 9 * * *";
    }
    return initial;
  });

  const [commitMutation, isMutationInFlight] = useMutation<CreateDigestWizardMutation>(createDigestMutation);

  const { containerRef: scrollContainerRef, showErrors: showValidationErrors, trigger: triggerValidationScroll, reset: resetValidation } = useScrollToFirstInvalid();
  const [validationErrorMessage, setValidationErrorMessage] = useState("");

  useEffect(() => {
    if (isOpen) {
      const initial = createInitialDigestFormState();
      if (initialSections && initialSections.length > 0) {
        initial.sections = mapToDigestSections(initialSections);
      }
      if (initialName) {
        initial.name = initialName;
      }
      if (initialCronExpression) {
        initial.cronExpression = initialCronExpression;
      } else if (isOnboarding) {
        initial.cronExpression = "0 9 * * *";
      }
      setState(initial);
      resetValidation();
      setValidationErrorMessage("");
    }
  }, [isOpen, initialSections, initialName, initialCronExpression, isOnboarding, resetValidation]);

  const updateState = useCallback((updates: Partial<DigestFormStateType>) => {
    setState((prev) => ({ ...prev, ...updates }));
  }, []);

  const handleClose = () => {
    setState(createInitialDigestFormState());
    onClose();
  };

  const localSectionsJson = useMemo(
    () => JSON.stringify({ sections: state.sections.map((s) => ({ section: s.section, text: s.text })), slack_channel_id: "" }),
    [state.sections],
  );

  const handleSectionsChange = useCallback((jsonString: string) => {
    try {
      const parsed = JSON.parse(jsonString);
      if (parsed.sections && Array.isArray(parsed.sections)) {
        updateState({
          sections: parsed.sections.map((s: { section: string; text: string }, index: number) => ({
            section: s.section || "",
            text: s.text || "",
            id: (index + 1).toString(),
          })),
        });
      }
    } catch {
      // ignore
    }
  }, [updateState]);

  const scheduleJson = JSON.stringify({ cron_expression: state.cronExpression });
  const [scheduleError, setScheduleError] = useState(false);

  const handleScheduleChange = useCallback((jsonString: string) => {
    try {
      const parsed = JSON.parse(jsonString);
      if (parsed.cron_expression !== undefined) {
        updateState({ cronExpression: parsed.cron_expression });
      }
    } catch {
      // ignore
    }
  }, [updateState]);

  const isReasoningPickerEnabled = useFeatureFlag("DIGEST_EFFORT_LEVEL");

  const handleSubmit = () => {
    if (!teamId) {
      toast({ title: "Error", description: "Please select a team", variant: "error" });
      return;
    }

    const hasName = !!state.name?.trim();
    const hasSections = state.sections.every((s) => s.section.trim() !== "" && s.text.trim() !== "");
    const hasCron = !!state.cronExpression && !scheduleError;

    if (!hasName || !hasSections || !hasCron) {
      const errors: string[] = [];
      if (!hasName) errors.push("- Digest name is required");
      if (!hasSections) errors.push("- All sections must have a name and prompt");
      if (!hasCron) errors.push("- Please set a valid cron expression for the schedule");
      setValidationErrorMessage(`Please fix the following errors:\n${errors.join("\n")}`);
      triggerValidationScroll();
      return;
    }
    setValidationErrorMessage("");

    const inputJson: Record<string, unknown> = {
      sections: state.sections.map((s) => ({ section: s.section, text: s.text })),
      notification_format: state.notificationFormat,
    };
    if (isReasoningPickerEnabled) {
      inputJson.reasoning = state.reasoning;
    }

    commitMutation({
      variables: {
        input: {
          teamId,
          name: state.name,
          inputJson,
          schedule: { cron_expression: state.cronExpression },
          taskType: "DIGEST",
          slackChannelIds: state.selectedSlackChannels.map((ch) => ch.id),
          notificationIntegrationIds: state.selectedDiscordWebhooks.map((wh) => wh.id),
        },
      },
      onCompleted: (response, errors) => {
        if (errors && errors.length > 0) {
          toast({
            title: "Error creating digest agent",
            description: errors.map((e) => e.message).join(", "),
            variant: "error",
          });
        } else if (response.createJob?.job?.id) {
          toast({
            title: "Digest agent created",
            description: `"${response.createJob.job.name}" has been created successfully.`,
            variant: "success",
          });
          if (onSuccess) {
            onSuccess(response.createJob.job.id);
          } else {
            handleClose();
          }
        }
      },
      onError: (error) => {
        toast({
          title: "Error creating digest agent",
          description: error.message || "Failed to create digest agent. Please try again.",
          variant: "error",
        });
      },
    });
  };

  return (
    <AnimatedDialog open={isOpen} onOpenChange={handleClose}>
      <AnimatedDialogContent variant="full" hideCloseButton={false} dismissOnOutsideClick={false}>
        <AnimatedDialogHeader>
          <AnimatedDialogTitle className="text-foreground text-2xl font-semibold">Create Digest Agent</AnimatedDialogTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Configure an agent to automatically generate reports on a schedule.
          </p>
        </AnimatedDialogHeader>

        {isOnboarding && (
          <UITourBanner
            text="Let's create your first digest agent to automatically generate reports."
            showBackButton={false}
            onboardingStep={onboardingStep}
          />
        )}

        <div ref={scrollContainerRef} className="mt-4">
          {validationErrorMessage && (
            <div className="sticky top-0 z-10 text-red-400 p-2 mb-4 border border-red-800 rounded-md whitespace-pre-line bg-background">
              {validationErrorMessage}
            </div>
          )}
          <Suspense
            fallback={
              <div className="flex items-center justify-center py-12">
                <LoadingSpinner className="h-8 w-8 text-muted-foreground" />
              </div>
            }
          >
            <SinglePageFormContent
              state={state}
              updateState={updateState}
              organizationId={organizationData.id}
              localSectionsJson={localSectionsJson}
              onSectionsChange={handleSectionsChange}
              scheduleJson={scheduleJson}
              onScheduleChange={handleScheduleChange}
              onScheduleError={setScheduleError}
              isReasoningPickerEnabled={isReasoningPickerEnabled}
              showValidationErrors={showValidationErrors}
            />
          </Suspense>
        </div>

        <div className="mt-6 pt-5 border-t border-border">
          <div className="flex justify-end items-center gap-3">
            <Button
              variant="outline"
              onClick={handleClose}
              className="px-6"
            >
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isMutationInFlight} size="lg" className="px-8">
              {isMutationInFlight ? "Creating..." : "Create Agent"}
            </Button>
          </div>
        </div>
      </AnimatedDialogContent>
    </AnimatedDialog>
  );
}

interface SinglePageFormContentProps {
  state: DigestFormStateType;
  updateState: (updates: Partial<DigestFormStateType>) => void;
  organizationId: string;
  localSectionsJson: string;
  onSectionsChange: (json: string) => void;
  scheduleJson: string;
  onScheduleChange: (json: string) => void;
  onScheduleError: (error: boolean) => void;
  isReasoningPickerEnabled: boolean;
  showValidationErrors: boolean;
}

function SinglePageFormContent({
  state,
  updateState,
  organizationId,
  localSectionsJson,
  onSectionsChange,
  scheduleJson,
  onScheduleChange,
  onScheduleError,
  isReasoningPickerEnabled,
  showValidationErrors,
}: SinglePageFormContentProps) {
  const data = useLazyLoadQuery<CreateDigestWizardIntegrationsQueryType>(
    integrationsQuery,
    { organizationId },
    { fetchPolicy: "store-or-network" },
  );

  const organization = data.node;

  const hasSlackIntegration =
    organization?.integrations?.edges?.some(
      (edge) => edge.node.integrationType === "SLACK" && edge.node.integrationStatus === "APPROVED",
    ) ?? false;

  const hasDiscordIntegration =
    organization?.integrations?.edges?.some(
      (edge) => edge.node.integrationType === "DISCORD" && edge.node.integrationStatus === "APPROVED",
    ) ?? false;

  const discordWebhooks = organization?.discordWebhooks?.edges || [];

  const nameInvalid = showValidationErrors && state.name.trim() === "";

  return (
    <div className="space-y-5">
      <FormSection number={1} title="Name" description="Give your digest agent a name to identify it.">
        <InvalidFieldTooltip invalid={nameInvalid} message="Digest name is required">
          <Input
            id="digest-name"
            value={state.name}
            onChange={(e) => updateState({ name: e.target.value })}
            placeholder="e.g. Daily Error Report"
            aria-invalid={nameInvalid}
            className={`bg-background border-border text-foreground ${nameInvalid ? "border-red-500" : ""}`}
          />
        </InvalidFieldTooltip>
      </FormSection>

      <div className="border-t border-border/50" />

      <FormSection number={2} title="Sections" description="Define what the agent should investigate and report on.">
        <CompactSectionBuilder
          value={localSectionsJson}
          onChange={onSectionsChange}
          showValidationErrors={showValidationErrors}
        />
      </FormSection>

      <div className="border-t border-border/50" />

      <FormSection number={3} title="Schedule" description="Set when your digest should run.">
        <ScheduleBuilder value={scheduleJson} onChange={onScheduleChange} onError={onScheduleError} />
      </FormSection>

      <div className="border-t border-border/50" />

      <FormSection number={4} title="Delivery" description="Choose where to send digest results.">
        <DigestNotificationDestinations
          hasSlackIntegration={hasSlackIntegration}
          hasDiscordIntegration={hasDiscordIntegration}
          selectedSlackChannels={state.selectedSlackChannels}
          selectedDiscordWebhooks={state.selectedDiscordWebhooks}
          availableDiscordWebhooks={discordWebhooks.map((edge) => ({
            id: edge.node.id,
            name: edge.node.name,
          }))}
          onSlackChannelAdd={(channel) => {
            updateState({ selectedSlackChannels: [...state.selectedSlackChannels, channel] });
          }}
          onSlackChannelRemove={(channelId) => {
            updateState({
              selectedSlackChannels: state.selectedSlackChannels.filter((ch) => ch.id !== channelId),
            });
          }}
          onDiscordWebhookAdd={(webhook) => {
            updateState({ selectedDiscordWebhooks: [...state.selectedDiscordWebhooks, webhook] });
          }}
          onDiscordWebhookRemove={(webhookId) => {
            updateState({
              selectedDiscordWebhooks: state.selectedDiscordWebhooks.filter((wh) => wh.id !== webhookId),
            });
          }}
          noIntegrationsVariant="banner"
        />

        <div className="mt-4">
          <Label className="text-sm text-muted-foreground mb-2 block">Format</Label>
          <Select
            value={state.notificationFormat}
            onValueChange={(value: "summary" | "full") => updateState({ notificationFormat: value })}
          >
            <SelectTrigger className="w-48 bg-background border-border">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="summary">Summary</SelectItem>
              <SelectItem value="full">Full Content</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </FormSection>

      {isReasoningPickerEnabled && (
        <>
          <div className="border-t border-border/50" />
          <FormSection number={5} title="Reasoning" description="Level of reasoning applied to each section.">
            <ReasoningPicker value={state.reasoning} onChange={(value) => updateState({ reasoning: value })} />
          </FormSection>
        </>
      )}
    </div>
  );
}
